/**
 * Modelo do formulário de usuário: schema, valores iniciais e conversão para o
 * corpo da requisição.
 *
 * Vive fora da tela porque `usuariosConfig` e `portalUsuariosConfig` continuam
 * usando as mesmas regras na LISTA. Uma fonte só evita que as duas divirjam.
 */
import { z } from 'zod'
import type { Usuario, UsuarioScopes } from '../../core/api/types'
import { ESCOPO_LIBERADO, type SelectionMode } from '../../shared/form/useSelectionField'

export const CUSTOMER_ROLES = ['customer_admin', 'customer_operator'] as const

/** Categorias de vínculo COM modo de acesso, na ORDEM das abas. */
export const CATEGORIAS_DE_ESCOPO = [
  'customers',
  'contracts',
  'personnel',
  'fleet',
  'places',
] as const
export type CategoriaDeEscopo = (typeof CATEGORIAS_DE_ESCOPO)[number]

/** Duplas é a sexta área, e a mais consequente para quem é da equipe.
 *
 *  Fica fora de CATEGORIAS_DE_ESCOPO de propósito: as cinco acima nascem em
 *  "todos", e duplas nasce restrita, porque é PERTENCIMENTO. Operador da equipe
 *  enxerga as duplas dele, e quem não tem nenhuma não enxerga nada, em lugar
 *  nenhum, por mais amplo que esteja o resto.
 *
 *  Desde 08/10/2026 ela também tem o modo "todas", `scope_all_duplas`: "toda dupla
 *  que entrar já fica para o usuário ver" (Samuel). */
export const AREA_DE_DUPLAS = 'duplas' as const
export type AreaDoFormulario = CategoriaDeEscopo | typeof AREA_DE_DUPLAS

/** O portal não configura clientes: o sub-operador já vive dentro de um só. */
export const CATEGORIAS_DO_PORTAL = CATEGORIAS_DE_ESCOPO.filter((c) => c !== 'customers')

export type Variante = 'backoffice' | 'portal'

export function categoriasDaVariante(variante: Variante): readonly CategoriaDeEscopo[] {
  return variante === 'portal' ? CATEGORIAS_DO_PORTAL : CATEGORIAS_DE_ESCOPO
}

const escopoSchema = z.object({
  mode: z.enum(['all', 'subset']),
  uuids: z.array(z.string()).default([]),
})

export const usuarioSchema = z
  .object({
    email: z.string().min(1, 'Informe o e-mail').email('E-mail inválido'),
    name: z.string().min(1, 'Informe o nome'),
    role: z.enum(['staff_admin', 'staff_operator', 'customer_admin', 'customer_operator']),
    // Lista, e não um só: o mesmo grupo aparece no cadastro como várias unidades
    // (Samuel, 21/09/2026). A PRIMEIRA é a principal, e é dela o que a pessoa criar
    // quando o próprio registro não disser o dono.
    tenant_uuids: z.array(z.string()).default([]),
    phone: z.string().optional(),
    is_active: z.boolean(),
    is_blocked: z.boolean(),
    // O cargo do operador da equipe (15/10/2026); vazio é "sem cargo". Com cargo,
    // `permissions` guarda só os adicionais: as do cargo aparecem travadas.
    cargo_uuid: z.string().default(''),
    // As áreas próprias (24/10/2026): com cargo, as adicionais; as do cargo
    // aparecem travadas, no mesmo desenho das permissões.
    areas: z.array(z.string()).default([]),
    permissions: z.array(z.string()).default([]),
    scopes: z.object({
      customers: escopoSchema,
      contracts: escopoSchema,
      personnel: escopoSchema,
      fleet: escopoSchema,
      places: escopoSchema,
      // Duplas nasce em `subset`; `all` é a marca "todas as duplas".
      duplas: escopoSchema,
    }),
  })
  .refine((v) => !CUSTOMER_ROLES.includes(v.role as never) || v.tenant_uuids.length > 0, {
    message: 'Selecione ao menos um cliente',
    path: ['tenant_uuids'],
  })

export type UsuarioFormValues = z.infer<typeof usuarioSchema>

/** Escopos frescos. Objeto NOVO por categoria: compartilhar a referência faria o
 *  `isDirty` do react-hook-form acusar sujeira em formulário intocado. */
function escoposLiberados() {
  const cinco = CATEGORIAS_DE_ESCOPO.map(
    (c) => [c, { ...ESCOPO_LIBERADO, uuids: [] as string[] }] as const,
  )
  return Object.fromEntries([
    ...cinco,
    [AREA_DE_DUPLAS, { mode: 'subset' as const, uuids: [] as string[] }],
  ]) as UsuarioFormValues['scopes']
}

export function usuarioVazio(variante: Variante): UsuarioFormValues {
  return {
    email: '',
    name: '',
    role: variante === 'portal' ? 'customer_operator' : 'staff_operator',
    tenant_uuids: [],
    phone: '',
    is_active: true,
    is_blocked: false,
    cargo_uuid: '',
    areas: [],
    permissions: [],
    scopes: escoposLiberados(),
  }
}

/** Usuário salvo → valores do formulário. */
export function usuarioParaForm(u: Usuario): UsuarioFormValues {
  const scopes = escoposLiberados()
  for (const categoria of CATEGORIAS_DE_ESCOPO) {
    const salvo = u.scope?.[categoria as keyof UsuarioScopes]
    // Ausência de `scope` na resposta significa acesso amplo, que é o padrão de
    // coluna do backend e o estado de todo usuário criado antes deste recurso.
    scopes[categoria] = {
      mode: (salvo && !salvo.all ? 'subset' : 'all') as SelectionMode,
      uuids: [],
    }
  }
  scopes[AREA_DE_DUPLAS] = { mode: u.scope?.duplas?.all ? 'all' : 'subset', uuids: [] }
  return {
    email: u.email,
    name: u.name,
    role: u.role,
    // A lista vem do servidor com a principal na frente. `customer_uuid` responde
    // sozinho por quem foi salvo antes de a lista existir.
    tenant_uuids: u.customers?.length
      ? u.customers.map((c) => c.uuid)
      : (u.customer_uuid ? [u.customer_uuid] : []),
    phone: u.phone ?? '',
    is_active: u.is_active,
    is_blocked: u.is_blocked,
    cargo_uuid: u.cargo?.uuid ?? '',
    // As próprias, e não as efetivas: com cargo, mandar as efetivas de volta
    // gravaria no usuário cópia do que vem do cargo.
    permissions: u.permissions_proprias ?? u.permissions ?? [],
    areas: u.areas_proprias ?? [],
    scopes,
  }
}

/** Categorias restritas sem nenhum item marcado. Vazio significa "pode salvar". */
export function escoposVazios(
  values: UsuarioFormValues,
  variante: Variante,
): CategoriaDeEscopo[] {
  if (values.role === 'staff_admin') return []
  return categoriasDaVariante(variante).filter((categoria) => {
    const escopo = values.scopes[categoria]
    return escopo.mode === 'subset' && escopo.uuids.length === 0
  })
}

/** True se o usuário é da equipe e ficaria sem nenhuma dupla.
 *
 *  Não bloqueia o salvamento, ao contrário das cinco categorias: existe operador
 *  que ainda não foi alocado, e travar o cadastro por isso atrapalharia. Mas
 *  precisa avisar, porque a consequência é dura e invisível: sem dupla, a Frente
 *  de Trabalho, o painel e as cargas aparecem vazios, mesmo com acesso amplo em
 *  todo o resto. */
export function operadorSemDupla(values: UsuarioFormValues, variante: Variante): boolean {
  if (variante === 'portal') return false
  if (values.role !== 'staff_operator') return false
  const duplas = values.scopes[AREA_DE_DUPLAS]
  return duplas.mode !== 'all' && duplas.uuids.length === 0
}

/** Chave do corpo com a lista de identificadores de cada categoria. */
const CHAVE_DE_UUIDS: Record<CategoriaDeEscopo, string> = {
  customers: 'customer_uuids',
  contracts: 'contract_uuids',
  personnel: 'personnel_uuids',
  fleet: 'fleet_uuids',
  places: 'place_uuids',
}

/** Chave do corpo com o modo de cada categoria. */
const CHAVE_DE_MODO: Record<CategoriaDeEscopo, string> = {
  customers: 'scope_all_customers',
  contracts: 'scope_all_contracts',
  personnel: 'scope_all_personnel',
  fleet: 'scope_all_fleet',
  places: 'scope_all_places',
}

export function usuarioParaBody(
  values: UsuarioFormValues,
  variante: Variante,
  opcoes: { confirmarEscopoVazio?: boolean } = {},
): Record<string, unknown> {
  const ehCliente = CUSTOMER_ROLES.includes(values.role as never)
  const body: Record<string, unknown> = {
    email: values.email,
    name: values.name,
    phone: values.phone || null,
    is_active: values.is_active,
    is_blocked: values.is_blocked,
    permissions: values.permissions ?? [],
  }
  // O portal não escolhe papel nem cliente: o backend força os dois. Mandá-los
  // daqui seria pedir uma recusa que a tela não tem como explicar.
  if (variante === 'backoffice') {
    body.role = values.role
    // Cargo é só de operador da equipe; nos outros papéis o servidor o tira.
    body.cargo_uuid = values.role === 'staff_operator' && values.cargo_uuid ? values.cargo_uuid : null
    // Área própria também é só do operador da equipe: o administrador entra em
    // todas, e o cliente não tem área.
    body.areas = values.role === 'staff_operator' ? (values.areas ?? []) : []
    body.tenant_uuids = ehCliente ? values.tenant_uuids : []
    // O principal continua viajando sozinho, para o servidor que ainda não conhece
    // a lista, e para deixar explícito de quem é o que a pessoa criar.
    body.customer_uuid = ehCliente ? (values.tenant_uuids[0] ?? null) : null
  }
  // Superusuário alcança tudo por bypass no servidor: mandar escopo aqui gravaria
  // um recorte que nunca é consultado, e a tela mostraria uma restrição que não
  // existe na prática.
  if (values.role !== 'staff_admin') {
    for (const categoria of categoriasDaVariante(variante)) {
      const escopo = values.scopes[categoria]
      body[CHAVE_DE_MODO[categoria]] = escopo.mode === 'all'
      if (escopo.mode === 'subset') body[CHAVE_DE_UUIDS[categoria]] = escopo.uuids
    }
  }
  // Duplas só existe para usuário de equipe: o backend recusa para cliente, e o
  // portal nem mostra a aba. Com "todas", o servidor limpa a lista sozinho.
  if (variante === 'backoffice' && !ehCliente && values.role !== 'staff_admin') {
    const duplas = values.scopes[AREA_DE_DUPLAS]
    body.scope_all_duplas = duplas.mode === 'all'
    if (duplas.mode === 'subset') body.dupla_uuids = duplas.uuids
  }
  if (opcoes.confirmarEscopoVazio) body.confirmar_escopo_vazio = true
  return body
}
